/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import * as $ from "jquery";
import {UiComponent} from "./UiComponent";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {applyDisplayMode} from "./Common";
import {executeWhenAttached} from "./util/ExecuteWhenAttached";
import {UiImageCropper_CropFrameChangedEvent, UiImageCropper_SelectionMode, UiImageCropperCommandHandler, UiImageCropperConfig, UiImageCropperEventSource} from "../generated/UiImageCropperConfig";
import {UiPageDisplayMode} from "../generated/UiPageDisplayMode";
import {EventFactory} from "../generated/EventFactory";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

export class UiImageCropper extends UiComponent<UiImageCropperConfig> implements UiImageCropperCommandHandler, UiImageCropperEventSource {

	public readonly onCropFrameChanged: TeamAppsEvent<UiImageCropper_CropFrameChangedEvent> = new TeamAppsEvent<UiImageCropper_CropFrameChangedEvent>(this);

	private $element: JQuery;
	private $croppingFrame: JQuery;
	private htmlImageElement: HTMLImageElement;

	private selectionLeft: number;
	private selectionTop: number;
	private selectionWidth: number;
	private selectionHeight: number;
	private imageNaturalWidth: number = 100;
	private imageNaturalHeight: number = 100;

	constructor(config: UiImageCropperConfig,
	            context: TeamAppsUiContext) {
		super(config, context);
		this.$element = $(`<div data-id="' + config.id + '" class="UiImageCropper">
        <img src="${config.imageUrl}"/>
        <div class="cropping-frame mode-${UiImageCropper_SelectionMode[config.selectionMode].toLowerCase()}" tabindex="-1"/>
</div>`);
		this.htmlImageElement = this.$element.find("img")[0] as HTMLImageElement;
		this.htmlImageElement.onload = () => {
			this.imageNaturalWidth = this.htmlImageElement.naturalWidth;
			this.imageNaturalHeight = this.htmlImageElement.naturalHeight;
			applyDisplayMode(this.getMainDomElement(), $(this.htmlImageElement), UiPageDisplayMode.FIT_SIZE);
			if (this.selectionLeft == null
				|| this.selectionTop == null
				|| this.selectionWidth == null
				|| this.selectionHeight == null) {
				let naturalImageAspectRatio = this.imageNaturalWidth / this.imageNaturalHeight;
				if (config.aspectRatio / naturalImageAspectRatio > 1) {
					this.selectionWidth = 0.8 * this.imageNaturalWidth;
					this.selectionHeight = this.selectionWidth / config.aspectRatio;
				} else {
					this.selectionHeight = 0.8 * this.imageNaturalHeight;
					this.selectionWidth = this.selectionHeight * config.aspectRatio;
				}

				this.selectionLeft = 0.5 * (this.imageNaturalWidth - this.selectionWidth);
				this.selectionTop = 0.5 * (this.imageNaturalHeight - this.selectionHeight);
			}
			this.updateCroppingFramePosition();
		};
		this.setImageUrl(config.imageUrl);
		// this.$element.css("background-image", `url(${config.imageUrl})`);
		this.$croppingFrame = this.$element.find(".cropping-frame")
			.resizable({
				handles: 'n, e, s, w, ne, se, nw, sw',
				aspectRatio: config.aspectRatio || undefined,
				containment: $(this.htmlImageElement),
				stop: this.handleDragEnd.bind(this)
			})
			.draggable({
				containment: $(this.htmlImageElement),
				stop: this.handleDragEnd.bind(this)
			});
	}

	private handleDragEnd() {
		let correctionFactor = this.calculateCoordinateCorrectionFactor();
		this.selectionLeft = (this.$croppingFrame[0].offsetLeft - this.htmlImageElement.offsetLeft) * correctionFactor;
		this.selectionTop = (this.$croppingFrame[0].offsetTop - this.htmlImageElement.offsetTop) * correctionFactor;
		this.selectionWidth = this.$croppingFrame[0].offsetWidth * correctionFactor;
		this.selectionHeight = this.$croppingFrame[0].offsetHeight * correctionFactor;

		this.logger.debug("selection: " + this.selectionLeft + ", " + this.selectionTop + ", " + this.selectionWidth + ", " + this.selectionHeight);
		this.onCropFrameChanged.fire(EventFactory.createUiImageCropper_CropFrameChangedEvent(this.getId(), this.selectionLeft, this.selectionTop, this.selectionWidth, this.selectionHeight));
	}

	private calculateCoordinateCorrectionFactor() {
		return Math.max(this.imageNaturalWidth / this.getWidth(), this.imageNaturalHeight / this.getHeight());
	}

	private setImageUrl(url: string) {
		this.htmlImageElement.src = url;
	}

	public setCroppedArea(left: number, top: number, width: number, height: number) {
		this.selectionLeft = left;
		this.selectionTop = top;
		this.selectionWidth = width;
		this.selectionHeight = height;
		this.updateCroppingFramePosition();
	}

	@executeWhenAttached(true)
	public onResize(): void {
		applyDisplayMode(this.getMainDomElement(), $(this.htmlImageElement), UiPageDisplayMode.FIT_SIZE, {
			innerPreferedDimensions: {
				width: this.imageNaturalWidth,
				height: this.imageNaturalHeight
			}
		});
		this.updateCroppingFramePosition();
	}

	@executeWhenAttached(true)
	private updateCroppingFramePosition() {
		let correctionFactor = this.calculateCoordinateCorrectionFactor();

		let left = this.selectionLeft / correctionFactor;
		let top = this.selectionTop / correctionFactor;
		let width = this.selectionWidth / correctionFactor;
		let height = this.selectionHeight / correctionFactor;

		this.$croppingFrame.css({
			left: left + (this.htmlImageElement.offsetLeft - this.getMainDomElement()[0].offsetLeft),
			top: top + (this.htmlImageElement.offsetTop - this.getMainDomElement()[0].offsetTop),
			width,
			height
		});
	}

	public getMainDomElement(): JQuery {
		return this.$element;
	}


	protected onAttachedToDom() {
		this.reLayout();
	}

	public destroy(): void {
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiImageCropper", UiImageCropper);